import { useState, useMemo, useEffect } from 'react'
import { usePreferences } from '../contexts/PreferencesContext'
import { Link } from 'react-router-dom'
import {
    Search,
    Filter,
    X,
    MapPin,
    Camera,
    Wifi,
    WifiOff,
    Star,
    Clock,
    ChevronDown,
    ChevronUp,
    Heart,
    Users,
    Play
} from 'lucide-react'

const AdvancedCameraSearch = ({
    cameras,
    searchTerm,
    onSearchChange,
    onCamerasFiltered,
    onCameraSelect,
    onCameraDoubleClick,
    selectedCameras = [],
    hasPlaybackPermission = false
}) => {
    const { preferences, toggleFavorite, addRecentCamera } = usePreferences()
    const [showFilters, setShowFilters] = useState(false)
    const [selectedStatuses, setSelectedStatuses] = useState(['online', 'offline'])
    const [selectedLocations, setSelectedLocations] = useState([])
    const [selectedTypes, setSelectedTypes] = useState([])
    const [showFavoritesOnly, setShowFavoritesOnly] = useState(false)
    const [showRecentOnly, setShowRecentOnly] = useState(false)
    const [groupByLocation, setGroupByLocation] = useState(preferences.filters.groupByLocation)

    // Get unique locations and types from cameras
    const locations = useMemo(() => {
        const locationSet = new Set()
        cameras.forEach(camera => {
            if (camera.location?.exactLocation) {
                locationSet.add(camera.location.exactLocation)
            }
        })
        return Array.from(locationSet).sort()
    }, [cameras])

    const cameraTypes = useMemo(() => {
        const typeSet = new Set()
        cameras.forEach(camera => {
            if (camera.type) {
                typeSet.add(camera.type)
            }
        })
        return Array.from(typeSet).sort()
    }, [cameras])

    // Filter cameras based on all criteria
    const filteredCameras = useMemo(() => {
        let filtered = cameras

        // Text search
        if (searchTerm) {
            const search = searchTerm.toLowerCase()
            filtered = filtered.filter(camera =>
                camera.name.toLowerCase().includes(search) ||
                camera.location?.exactLocation?.toLowerCase().includes(search) ||
                camera.description?.toLowerCase().includes(search)
            )
        }

        // Status filter
        filtered = filtered.filter(camera => {
            if (selectedStatuses.includes('online') && camera.isOnline) return true
            if (selectedStatuses.includes('offline') && !camera.isOnline) return true
            return false
        })

        // Location filter
        if (selectedLocations.length > 0) {
            filtered = filtered.filter(camera =>
                selectedLocations.includes(camera.location?.exactLocation)
            )
        }

        // Type filter
        if (selectedTypes.length > 0) {
            filtered = filtered.filter(camera =>
                selectedTypes.includes(camera.type)
            )
        }

        // Favorites filter
        if (showFavoritesOnly) {
            filtered = filtered.filter(camera =>
                preferences.favorites.includes(camera._id)
            )
        }

        // Recent filter
        if (showRecentOnly) {
            filtered = filtered.filter(camera =>
                preferences.recentCameras.includes(camera._id)
            )
        }

        return filtered
    }, [
        cameras,
        searchTerm,
        selectedStatuses,
        selectedLocations,
        selectedTypes,
        showFavoritesOnly,
        showRecentOnly,
        preferences.favorites,
        preferences.recentCameras
    ])

    // Group cameras by location if enabled
    const groupedCameras = useMemo(() => {
        if (!groupByLocation) {
            return { 'All Cameras': filteredCameras }
        }

        const groups = {}
        filteredCameras.forEach(camera => {
            const location = camera.location?.exactLocation || 'Unknown Location'
            if (!groups[location]) {
                groups[location] = []
            }
            groups[location].push(camera)
        })

        return groups
    }, [filteredCameras, groupByLocation])

    // Notify parent of filtered cameras
    useEffect(() => {
        onCamerasFiltered(filteredCameras)
    }, [filteredCameras, onCamerasFiltered])

    const handleStatusToggle = (status) => {
        setSelectedStatuses(prev =>
            prev.includes(status)
                ? prev.filter(s => s !== status)
                : [...prev, status]
        )
    }

    const handleLocationToggle = (location) => {
        setSelectedLocations(prev =>
            prev.includes(location)
                ? prev.filter(l => l !== location)
                : [...prev, location]
        )
    }

    const handleTypeToggle = (type) => {
        setSelectedTypes(prev =>
            prev.includes(type)
                ? prev.filter(t => t !== type)
                : [...prev, type]
        )
    }

    const clearAllFilters = () => {
        setSelectedStatuses(['online', 'offline'])
        setSelectedLocations([])
        setSelectedTypes([])
        setShowFavoritesOnly(false)
        setShowRecentOnly(false)
        onSearchChange('')
    }

    const getActiveFilterCount = () => {
        let count = 0
        if (searchTerm) count++
        if (selectedStatuses.length < 2) count++
        if (selectedLocations.length > 0) count++
        if (selectedTypes.length > 0) count++
        if (showFavoritesOnly) count++
        if (showRecentOnly) count++
        return count
    }

    const highlightText = (text, searchTerm) => {
        if (!searchTerm || !text) return text

        const regex = new RegExp(`(${searchTerm})`, 'gi')
        const parts = text.split(regex)

        return parts.map((part, index) =>
            regex.test(part) ? (
                <mark key={index} className="bg-yellow-200 dark:bg-yellow-800 px-1 rounded">
                    {part}
                </mark>
            ) : (
                part
            )
        )
    }

    return (
        <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                    type="text"
                    placeholder="Search cameras by name, location..."
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                {searchTerm && (
                    <button
                        onClick={() => onSearchChange('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>

            {/* Filter Toggle */}
            <div className="flex items-center justify-between">
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center space-x-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                >
                    <Filter className="h-4 w-4" />
                    <span>Filters</span>
                    {getActiveFilterCount() > 0 && (
                        <span className="bg-indigo-600 text-white text-xs px-2 py-1 rounded-full">
                            {getActiveFilterCount()}
                        </span>
                    )}
                    {showFilters ? (
                        <ChevronUp className="h-4 w-4" />
                    ) : (
                        <ChevronDown className="h-4 w-4" />
                    )}
                </button>

                {getActiveFilterCount() > 0 && (
                    <button
                        onClick={clearAllFilters}
                        className="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
                    >
                        Clear all
                    </button>
                )}
            </div>

            {/* Advanced Filters */}
            {showFilters && (
                <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg space-y-4">
                    {/* Quick Filters */}
                    <div className="space-y-2">
                        <h4 className="font-medium text-gray-900 dark:text-gray-100">Quick Filters</h4>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                                className={`px-3 py-1 rounded-full text-sm transition-colors ${showFavoritesOnly
                                        ? 'bg-red-500 text-white'
                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                                    }`}
                            >
                                <Star className="h-3 w-3 inline mr-1" />
                                Favorites Only
                            </button>
                            <button
                                onClick={() => setShowRecentOnly(!showRecentOnly)}
                                className={`px-3 py-1 rounded-full text-sm transition-colors ${showRecentOnly
                                        ? 'bg-blue-500 text-white'
                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                                    }`}
                            >
                                <Clock className="h-3 w-3 inline mr-1" />
                                Recent Only
                            </button>
                            <button
                                onClick={() => setGroupByLocation(!groupByLocation)}
                                className={`px-3 py-1 rounded-full text-sm transition-colors ${groupByLocation
                                        ? 'bg-green-500 text-white'
                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                                    }`}
                            >
                                <Users className="h-3 w-3 inline mr-1" />
                                Group by Location
                            </button>
                        </div>
                    </div>

                    {/* Status Filter */}
                    <div className="space-y-2">
                        <h4 className="font-medium text-gray-900 dark:text-gray-100">Status</h4>
                        <div className="flex space-x-3">
                            <button
                                onClick={() => handleStatusToggle('online')}
                                className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${selectedStatuses.includes('online')
                                        ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                    }`}
                            >
                                <Wifi className="h-4 w-4" />
                                <span>Online</span>
                            </button>
                            <button
                                onClick={() => handleStatusToggle('offline')}
                                className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${selectedStatuses.includes('offline')
                                        ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                    }`}
                            >
                                <WifiOff className="h-4 w-4" />
                                <span>Offline</span>
                            </button>
                        </div>
                    </div>

                    {/* Location Filter */}
                    {locations.length > 0 && (
                        <div className="space-y-2">
                            <h4 className="font-medium text-gray-900 dark:text-gray-100">Locations</h4>
                            <div className="flex flex-wrap gap-2">
                                {locations.map(location => (
                                    <button
                                        key={location}
                                        onClick={() => handleLocationToggle(location)}
                                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${selectedLocations.includes(location)
                                                ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                                                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                                            }`}
                                    >
                                        <MapPin className="h-3 w-3" />
                                        <span className="text-sm">{location}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Type Filter */}
                    {cameraTypes.length > 0 && (
                        <div className="space-y-2">
                            <h4 className="font-medium text-gray-900 dark:text-gray-100">Camera Types</h4>
                            <div className="flex flex-wrap gap-2">
                                {cameraTypes.map(type => (
                                    <button
                                        key={type}
                                        onClick={() => handleTypeToggle(type)}
                                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors ${selectedTypes.includes(type)
                                                ? 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200'
                                                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                                            }`}
                                    >
                                        <Camera className="h-3 w-3" />
                                        <span className="text-sm">{type}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Results Summary */}
            <div className="text-sm text-gray-500 dark:text-gray-400">
                Showing {filteredCameras.length} of {cameras.length} cameras
                {groupByLocation && Object.keys(groupedCameras).length > 1 &&
                    ` in ${Object.keys(groupedCameras).length} locations`
                }
            </div>

            {/* Grouped Camera List */}
            <div className="space-y-4">
                {Object.entries(groupedCameras).map(([groupName, groupCameras]) => (
                    <div key={groupName} className="space-y-2">
                        {groupByLocation && Object.keys(groupedCameras).length > 1 && (
                            <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center space-x-2">
                                <MapPin className="h-4 w-4" />
                                <span>{groupName} ({groupCameras.length})</span>
                            </h3>
                        )}
                        <div className="space-y-1">
                            {groupCameras.map((camera) => (
                                <CameraListItem
                                    key={camera._id}
                                    camera={camera}
                                    searchTerm={searchTerm}
                                    highlightText={highlightText}
                                    isFavorite={preferences.favorites.includes(camera._id)}
                                    isSelected={selectedCameras.some(c => c._id === camera._id)}
                                    onToggleFavorite={() => toggleFavorite(camera._id)}
                                    onSelect={() => {
                                        addRecentCamera(camera._id);
                                        onCameraSelect?.(camera);
                                    }}
                                    onDoubleClick={() => onCameraDoubleClick?.(camera)}
                                    hasPlaybackPermission={hasPlaybackPermission}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

const CameraListItem = ({
    camera,
    searchTerm,
    highlightText,
    isFavorite,
    isSelected,
    onToggleFavorite,
    onSelect,
    onDoubleClick,
    hasPlaybackPermission
}) => {
    return (
        <div
            className={`flex items-center space-x-3 p-2 rounded-lg border hover:shadow-md transition-all cursor-pointer ${isSelected
                    ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-400 dark:border-indigo-600'
                    : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                }`}
            onClick={onSelect}
            onDoubleClick={onDoubleClick}
        >
            <div className={`w-3 h-3 rounded-full ${camera.isOnline ? 'bg-green-500' : 'bg-red-500'}`} />

            <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 dark:text-gray-100">
                    {highlightText(camera.name, searchTerm)}
                </div>
                {camera.location?.exactLocation && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                        <MapPin className="h-3 w-3 inline mr-1" />
                        {highlightText(camera.location.exactLocation, searchTerm)}
                    </div>
                )}
            </div>

            <div className="flex items-center space-x-2">
                {isSelected && (
                    <div className="bg-indigo-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                        Selected
                    </div>
                )}

                {/* Playback Button */}
                {hasPlaybackPermission && (
                    <Link
                        to={`/camera-playback?cameraId=${camera._id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center justify-center p-1.5 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                        title="Open Playback"
                    >
                        <Play className="h-4 w-4" />
                    </Link>
                )}

                <button
                    onClick={(e) => {
                        e.stopPropagation()
                        onToggleFavorite()
                    }}
                    className={`p-1 rounded-full transition-colors ${isFavorite
                            ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
                            : 'text-gray-400 hover:text-red-500 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                >
                    <Heart className={`h-4 w-4 ${isFavorite ? 'fill-current' : ''}`} />
                </button>
            </div>
        </div>
    )
}

export default AdvancedCameraSearch